import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdTune, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { signalsService } from '../../services/signalsService';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import SymbolSearchAdd from './components/SymbolSearchAdd';

const formatNum = (v, dec = 2) =>
  v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(dec);

const colorPct = (v) =>
  v == null
    ? 'text-gray-400'
    : v > 0
    ? 'text-green-600 dark:text-green-400'
    : v < 0
    ? 'text-red-500 dark:text-red-400'
    : 'text-gray-500';

// Map a bucket score to the same Buy / Lean Buy / Neutral / Lean Sell / Sell
// labels we use for the composite. Same thresholds: 0.40 / 0.15.
const scoreToSignal = (score) => {
  if (score == null) return { code: 'HOLD', label: 'Neutral' };
  if (score >= 0.40) return { code: 'BUY', label: 'Buy' };
  if (score >= 0.15) return { code: 'WEAK_BUY', label: 'Lean Buy' };
  if (score > -0.15) return { code: 'HOLD', label: 'Neutral' };
  if (score > -0.40) return { code: 'WEAK_SELL', label: 'Lean Sell' };
  return { code: 'SELL', label: 'Sell' };
};

const SIGNAL_BADGE_CLASS = {
  BUY:       'bg-green-600 text-white',
  WEAK_BUY:  'bg-green-400/80 text-green-950',
  HOLD:      'bg-gray-500 text-white',
  WEAK_SELL: 'bg-red-400/80 text-red-950',
  SELL:      'bg-red-600 text-white',
};

const PickCard = ({ row, kind, onClick }) => {
  const isMomentum = kind.startsWith('momentum');
  const score = isMomentum ? row.momentum_score : row.mean_reversion_score;
  const { code, label } = scoreToSignal(score);
  const scoreColor = score > 0
    ? 'text-green-600 dark:text-green-400'
    : score < 0
    ? 'text-red-500 dark:text-red-400'
    : 'text-gray-500';

  const reading = isMomentum
    ? `MACD ${formatNum(row.macd_hist, 3)} · ADX ${formatNum(row.adx, 1)}`
    : `RSI ${formatNum(row.rsi_14, 1)} · BB% ${formatNum(row.bb_pct, 2)}`;

  const fmtScore = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}`);

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 hover:border-[#c9a96e] rounded-lg p-2.5 md:p-3 transition-colors"
    >
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-[#c9a96e] text-sm">{row.symbol}</span>
          <span className="text-[11px] md:text-xs text-gray-500 dark:text-gray-400 truncate">{row.name}</span>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold whitespace-nowrap ${SIGNAL_BADGE_CLASS[code]}`}>
          {label}
        </span>
      </div>
      <div className="flex items-center justify-between text-[10px] md:text-[11px]">
        <div className="text-gray-500 dark:text-gray-400 truncate">{reading}</div>
        <div className={`font-semibold ${scoreColor}`}>{fmtScore(score)}</div>
      </div>
      <div className="flex items-center justify-between text-[10px] md:text-[11px] mt-0.5">
        <div className="text-gray-500 dark:text-gray-400 truncate">
          {row.sector || (row.is_etf ? 'ETF' : '—')}
        </div>
        <div className={colorPct(row.pct_change)}>
          {formatNum(row.close, 2)}{' '}
          {row.pct_change != null && (
            <span>· {row.pct_change >= 0 ? '+' : ''}{formatNum(row.pct_change, 2)}%</span>
          )}
        </div>
      </div>
    </button>
  );
};

const PickColumn = ({ title, subtitle, kind, rows, accent, navigate }) => {
  const accentClass = accent === 'green'
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-500 dark:text-red-400';
  return (
    <div>
      <div className="mb-3">
        <div className={`text-xs uppercase tracking-[2px] font-semibold ${accentClass}`}>{title}</div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</div>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && (
          <div className="text-xs text-gray-400 italic py-6 text-center">
            None right now.
          </div>
        )}
        {rows.map((r) => (
          <PickCard
            key={r.symbol}
            row={r}
            kind={kind}
            onClick={() => navigate(`/signals/symbol/${r.symbol}`)}
          />
        ))}
      </div>
    </div>
  );
};

const BestPicksPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ is_etf: 'false', min_close: 5, index: '', sector: '' });
  const [limit, setLimit] = useState(5);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [meta, setMeta] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit, min_close: filter.min_close };
      if (filter.is_etf) params.is_etf = filter.is_etf;
      if (filter.index) params.index = filter.index;
      if (filter.sector) params.sector = filter.sector;
      const res = await signalsService.getBestPicks(params);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [filter, limit]);

  useEffect(() => { load(); }, [load]);

  // Fetch meta once for sector list + index counts.
  useEffect(() => {
    signalsService.getMeta().then(res => setMeta(res.data)).catch(() => {});
  }, []);

  if (loading) return <LoadingSpinner message="Ranking universe…" />;
  if (error) return <div className="text-sm text-red-500 p-4">{error}</div>;
  if (!data) return null;

  const m = data.momentum || {};
  const r = data.mean_reversion || {};

  return (
    <div>
      <SymbolSearchAdd className="mb-4" />

      {/* Filter expander */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg mb-4">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 text-xs"
        >
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 min-w-0">
            <MdTune className="shrink-0" />
            <span className="uppercase tracking-wider text-[10px] shrink-0">Filters</span>
            <span className="text-[10px] text-gray-400 truncate">
              · {filter.is_etf === 'false' ? 'Stocks' : filter.is_etf === 'true' ? 'ETFs' : 'Stocks + ETFs'}
              {filter.index === 'sp500' ? ' · S&P 500' : filter.index === 'nasdaq100' ? ' · Nasdaq 100' : filter.index === 'dow30' ? ' · Dow 30' : ''}
              {filter.sector ? ` · ${filter.sector}` : ''}
              {filter.min_close ? ` · $${filter.min_close}+` : ''}
              {' · top '}{limit}
            </span>
          </div>
          {filtersOpen ? <MdExpandLess className="text-gray-400 shrink-0" /> : <MdExpandMore className="text-gray-400 shrink-0" />}
        </button>
        {filtersOpen && (
          <div className="px-3 pb-3 grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-3 md:items-center text-xs border-t border-gray-100 dark:border-gray-800 pt-3">
            <select
              value={filter.is_etf}
              onChange={(e) => setFilter((f) => ({ ...f, is_etf: e.target.value }))}
              className="px-2 py-1 bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
            >
              <option value="false">Stocks only</option>
              <option value="true">ETFs only</option>
              <option value="">Stocks + ETFs</option>
            </select>
            <select
              value={filter.index}
              onChange={(e) => setFilter((f) => ({ ...f, index: e.target.value }))}
              className="px-2 py-1 bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
              title="Index membership filter"
            >
              <option value="">All indices</option>
              <option value="sp500">S&P 500{meta?.index_counts?.sp500 ? ` (${meta.index_counts.sp500})` : ''}</option>
              <option value="nasdaq100">Nasdaq 100{meta?.index_counts?.nasdaq100 ? ` (${meta.index_counts.nasdaq100})` : ''}</option>
              <option value="dow30">Dow 30{meta?.index_counts?.dow30 ? ` (${meta.index_counts.dow30})` : ''}</option>
            </select>
            <select
              value={filter.sector}
              onChange={(e) => setFilter((f) => ({ ...f, sector: e.target.value }))}
              className="px-2 py-1 bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
            >
              <option value="">All sectors</option>
              {(meta?.sectors || []).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-gray-500 dark:text-gray-400 col-span-2 md:col-span-1">
              Min $
              <input
                type="number"
                min="0" step="1"
                value={filter.min_close}
                onChange={(e) => setFilter((f) => ({ ...f, min_close: e.target.value }))}
                className="w-16 px-2 py-1 bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
              />
            </label>
            <label className="flex items-center gap-2 text-gray-500 dark:text-gray-400 col-span-2 md:col-span-1">
              Top
              <select
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                className="px-2 py-1 bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
            </label>
          </div>
        )}
      </div>

      {/* Momentum row */}
      <div className="mb-6">
        <div className="font-serif text-base md:text-lg text-gray-900 dark:text-gray-100 mb-1">Momentum</div>
        <div className="hidden md:block text-xs text-gray-500 dark:text-gray-400 mb-3">
          Trend-following signals: SMA 50/200 cross, EMA 12/26, MACD, ADX, OBV, price vs SMA 200.
          Rides strength up, fades weakness down.
        </div>
        <div className="md:hidden text-[11px] text-gray-500 dark:text-gray-400 mb-3">
          Trend-followers: ride strength up, fade weakness down.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PickColumn
            title="Best Momentum BUY"
            subtitle="Strongest trends to ride higher"
            kind="momentum_buy"
            rows={m.buy || []}
            accent="green"
            navigate={navigate}
          />
          <PickColumn
            title="Best Momentum SELL"
            subtitle="Weakest trends to fade or short"
            kind="momentum_sell"
            rows={m.sell || []}
            accent="red"
            navigate={navigate}
          />
        </div>
      </div>

      {/* Mean Reversion row */}
      <div className="mb-6">
        <div className="font-serif text-base md:text-lg text-gray-900 dark:text-gray-100 mb-1">Mean Reversion</div>
        <div className="hidden md:block text-xs text-gray-500 dark:text-gray-400 mb-3">
          Oscillator signals: RSI, Bollinger, Stochastic, Williams %R, Fibonacci.
          Buys oversold extremes, sells overbought extremes.
        </div>
        <div className="md:hidden text-[11px] text-gray-500 dark:text-gray-400 mb-3">
          Oscillators: buy oversold extremes, sell overbought.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PickColumn
            title="Best Mean-Rev BUY"
            subtitle="Most oversold — bounce candidates"
            kind="meanrev_buy"
            rows={r.buy || []}
            accent="green"
            navigate={navigate}
          />
          <PickColumn
            title="Best Mean-Rev SELL"
            subtitle="Most overbought — fade candidates"
            kind="meanrev_sell"
            rows={r.sell || []}
            accent="red"
            navigate={navigate}
          />
        </div>
      </div>
    </div>
  );
};

export default BestPicksPage;
